import { useEffect, useState } from "react";
import { DEFAULT_FIELDS, type Field } from "../../shared/types";
import { apiFor } from "./api";
import { useTournament } from "./tournament";

/**
 * Module-level store shared by every subscriber: `useFieldName` is called from
 * deep render trees (overlay chips, bridge header), and giving each caller its
 * own fetch + 60s poll multiplied requests by the number of chips on screen.
 * One interval runs while at least one component subscribes.
 *
 * ONE STORE PER TOURNAMENT (V2 Phase 2): a browser that opens two events in
 * one session must not label the second one's pitches with the first one's
 * names, and the poll for an event nobody is looking at must stop.
 */
interface Store {
  cache: Field[];
  subscribers: Set<(fields: Field[]) => void>;
  timer: ReturnType<typeof setInterval> | undefined;
}

const stores = new Map<string, Store>();

function storeFor(tid: string): Store {
  let store = stores.get(tid);
  if (store === undefined) {
    store = { cache: DEFAULT_FIELDS, subscribers: new Set(), timer: undefined };
    stores.set(tid, store);
  }
  return store;
}

function load(tid: string): void {
  const store = storeFor(tid);
  void apiFor(tid)
    .fields()
    .then((f) => {
      if (f.length === 0) return;
      store.cache = f;
      for (const notify of store.subscribers) notify(f);
    })
    .catch(() => undefined);
}

function subscribe(tid: string, notify: (fields: Field[]) => void): () => void {
  const store = storeFor(tid);
  store.subscribers.add(notify);
  if (store.subscribers.size === 1) {
    load(tid);
    store.timer = setInterval(() => load(tid), 60_000);
  }
  return () => {
    store.subscribers.delete(notify);
    if (store.subscribers.size === 0) {
      clearInterval(store.timer);
      store.timer = undefined;
    }
  };
}

/**
 * Configured fields of the tournament this surface is rendered for, polled
 * like the rest of the tournament data.
 *
 * Falls back to the built-in pair rather than an empty list: every surface
 * here renders per-field, and a blank screen while the first request is in
 * flight is worse than briefly showing the defaults the server also returns
 * when nothing is configured.
 */
export function useFields(): Field[] {
  const { id } = useTournament();
  const [fields, setFields] = useState<Field[]>(() => storeFor(id).cache);
  useEffect(() => {
    setFields(storeFor(id).cache);
    return subscribe(id, setFields);
  }, [id]);
  return fields;
}

/**
 * Display name for one field. Deep render trees (overlay chips, the bridge
 * header) need the name without threading the whole list through every prop.
 */
export function useFieldName(id: string): string {
  const fields = useFields();
  return fields.find((f) => f.id === id)?.name ?? `Field ${id}`;
}
